import React, { FC } from 'react';
import { View } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Svg from 'react-native-svg';
import { isWeb } from '@helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { Annotation, AnnotationPoint, CanvasCursor, GestureLikeEvent, ImageTransformInputs } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import type { ImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/helpers/imageViewTransformStore';
import AnnotationShape from '@v2/components/imageHandling/components/DrawingCanvas/components/AnnotationShape';
import EraserStrikethrough from '@v2/components/imageHandling/components/DrawingCanvas/components/EraserStrikethrough';
import SelectionHandles from '@v2/components/imageHandling/components/DrawingCanvas/components/SelectionHandles';
import { useManageAnnotationLayer } from './hooks/useManageAnnotationLayer';

interface AnnotationLayerProps {
  annotations: Annotation[];
  draftAnnotation: Annotation | null;
  selectedId: string | null;
  hoveredAnnotationId: string | null;
  eraserHoverId: string | null;
  editingTextId: string | null;
  /** Live text for the annotation currently being edited — drives the highlight rect width while typing. */
  editingText?: string | null;
  transformInputs: ImageTransformInputs;
  cursor: CanvasCursor;
  isAnnotating: boolean;
  viewTransform: ImageViewTransform;
  onPointerDown?: (point: AnnotationPoint, e: GestureLikeEvent) => void;
  onPointerMove?: (point: AnnotationPoint, e: GestureLikeEvent) => void;
  onPointerUp?: (e: { pointerId?: number }) => void;
  /** Double-click / double-tap. Caller is responsible for hit-test before forwarding to image zoom. */
  onDoubleClick?: (point: AnnotationPoint, e: { clientX: number; clientY: number }) => void;
  /**
   * Web only — when provided, this ref is bound to the layer's outer div so the controller's
   * non-passive wheel listener has a node to attach to (used for zoom + page-scroll suppression).
   */
  webContainerRefExternal?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * Native only — when true, the layer is rendered inside a parent that already applies the
   * image's zoom/pan transform (Zoomable). Skip the self-transform so we don't double-apply it.
   */
  insideZoomable?: boolean;
  /** Reports the layer's own rendered dimensions so the SVG viewBox + gesture coords stay aligned. */
  onViewportLayout?: (viewport: { viewportWidth: number; viewportHeight: number }) => void;
  /** Hit-test threshold in working-space units — matches the value used by `usePointerInteractions`. */
  hitThreshold: number;
}

const AnnotationLayer: FC<AnnotationLayerProps> = ({
  annotations,
  draftAnnotation,
  selectedId,
  hoveredAnnotationId,
  eraserHoverId,
  editingTextId,
  editingText,
  transformInputs,
  cursor,
  isAnnotating,
  viewTransform,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onDoubleClick,
  webContainerRefExternal,
  insideZoomable,
  onViewportLayout,
  hitThreshold,
}) => {
  const {
    containerPointerEvents,
    setWebContainerRef,
    handleWebPointerDown,
    handleWebPointerMove,
    handleWebPointerUp,
    handleWebPointerLeave,
    handleWebDoubleClick,
    nativeGesture,
  } = useManageAnnotationLayer({
    isAnnotating,
    viewTransform,
    annotations,
    hitThreshold,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onDoubleClick,
    webContainerRefExternal,
  });

  const viewBox = `0 0 ${transformInputs.viewportWidth} ${transformInputs.viewportHeight}`;

  const svgContent = (
    <Svg width="100%" height="100%" viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
      {annotations.map((a) => (
        <AnnotationShape
          key={a.id}
          annotation={a}
          isSelected={a.id === selectedId}
          isEraserHover={a.id === eraserHoverId}
          isHovered={a.id === hoveredAnnotationId && a.id !== selectedId}
          isEditing={a.id === editingTextId}
          editingText={a.id === editingTextId ? editingText : undefined}
          documentRotation={transformInputs.documentRotation}
        />
      ))}
      {draftAnnotation ? (
        <AnnotationShape
          annotation={draftAnnotation}
          isSelected={false}
          isEraserHover={false}
          isHovered={false}
          isEditing={false}
          documentRotation={transformInputs.documentRotation}
        />
      ) : null}
      {eraserHoverId
        ? (() => {
            const a = annotations.find((x) => x.id === eraserHoverId);
            return a ? <EraserStrikethrough annotation={a} /> : null;
          })()
        : null}
      {selectedId && !editingTextId
        ? (() => {
            const a = annotations.find((x) => x.id === selectedId);
            return a ? <SelectionHandles annotation={a} zoom={viewTransform.zoom} documentRotation={transformInputs.documentRotation} /> : null;
          })()
        : null}
    </Svg>
  );

  if (isWeb) {
    return (
      <div
        ref={setWebContainerRef}
        data-testid={TEST_IDS.drawingCanvas.layer}
        onPointerDown={handleWebPointerDown}
        onPointerMove={handleWebPointerMove}
        onPointerUp={handleWebPointerUp}
        onPointerLeave={handleWebPointerLeave}
        onDoubleClick={handleWebDoubleClick}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          cursor,
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          pointerEvents: containerPointerEvents,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform: `translate(${viewTransform.panX}px, ${viewTransform.panY}px) scale(${viewTransform.zoom || 1})`,
            transformOrigin: 'center center',
            pointerEvents: 'auto',
          }}
        >
          {svgContent}
        </div>
      </div>
    );
  }

  return (
    <GestureDetector gesture={nativeGesture}>
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // Skip the self-transform when rendered inside Zoomable (the parent already applies it).
          ...(insideZoomable
            ? null
            : { transform: [{ translateX: viewTransform.panX }, { translateY: viewTransform.panY }, { scale: viewTransform.zoom || 1 }] }),
        }}
        pointerEvents={isAnnotating ? 'auto' : 'none'}
        testID={TEST_IDS.drawingCanvas.layer}
        onLayout={(e) => {
          const { width, height } = e.nativeEvent.layout;
          onViewportLayout?.({ viewportWidth: width, viewportHeight: height });
        }}
      >
        {svgContent}
      </View>
    </GestureDetector>
  );
};

export default AnnotationLayer;
